import { ReactNode, useEffect, useRef } from 'react';
import { getIDFromUrl } from '@rossum/api-client';
import {
  alpha,
  Box,
  Chip,
  ChipProps,
  Skeleton,
  Stack,
  StackProps,
  useTheme,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { Url } from '../../types/basic';
import { usePageContent } from '../page-previews/usePageContent';

const A4_RATIO = 210 / 297;

// This component is used in both left and right part of the split pane
export const DocumentPage = ({
  pageUrl,
  pageNumber,
  isCurrent,
  onClick,
  rotationDeg,
  overlay,
  blackOverlay,
  imageContainerProps,
  pageChipProps,
}: {
  pageUrl: Url;
  pageNumber: number;
  isCurrent: boolean;
  onClick: () => void;
  rotationDeg: number;
  overlay?: ReactNode;
  blackOverlay: number;
  imageContainerProps?: StackProps;
  pageChipProps?: ChipProps;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const pageId = getIDFromUrl(pageUrl);
  const contentUrl = `${pageUrl}/content`;

  const { data, isSuccess } = usePageContent(contentUrl, pageId, {
    enabled: true,
  });

  // TODO: Revisit this logic
  const isLeftPane = !imageContainerProps;

  useEffect(() => {
    if (isCurrent) {
      // NOTE: It's important that scrolling container doesn't have scrollBehavior: 'smooth',
      // because it interferes with scrolling by dragging as implemented in dnd-kit.
      // That's why we set behavior here instead.
      // scrollIntoView with scrollBehavior: smooth doesn't work on multiple elements at the same time
      // https://stackoverflow.com/questions/49318497/google-chrome-simultaneously-smooth-scrollintoview-with-more-elements-doesn
      // For now, we use auto on the left part.
      // TODO: scrollTo() should be a workaround according to that SO question

      ref.current?.scrollIntoView({
        behavior: isLeftPane ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }
  }, [isCurrent, isLeftPane]);

  // 'image' is a blob URL, so when it's present, the image data has been already loaded
  const isLoaded = isSuccess;

  const { shape } = useTheme();

  if (!isLoaded) {
    return (
      <Stack
        spacing={1}
        sx={{ height: '100%', scrollMargin: '32px' }}
        ref={ref}
      >
        <Box
          sx={{
            flex: '1 0',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
            border: theme =>
              `1px solid ${alpha(
                theme.palette.mode === 'light'
                  ? theme.palette.common.black
                  : theme.palette.common.white,
                0.23
              )}`,
            borderRadius: borderRadius('s'),
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            sx={{ aspectRatio: `${A4_RATIO} / 1` }}
          />
        </Box>
        <Chip label="..." sx={{ alignSelf: 'center' }} />
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      onClick={onClick}
      sx={{ height: '100%', scrollMargin: '32px' }}
      ref={ref}
    >
      <Box
        sx={{
          flex: '1 0',
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          position: 'relative',
          '&:hover + div': {
            color: 'primary.contrastText',
            backgroundColor: 'primary.dark',
          },
          pointer: 'cursor',
          border: theme =>
            `1px solid ${alpha(
              theme.palette.mode === 'light'
                ? theme.palette.common.black
                : theme.palette.common.white,
              0.23
            )}`,
          // used to be border radius of pages (4px) + width of the border (1px) for some reason...
          // revert back if somebody complains
          borderRadius: borderRadius('s'),
        }}
        {...imageContainerProps}
      >
        <Box
          sx={{
            flex: '1 0',
            transition: 'all 0.3s',
            transform: `rotate(${rotationDeg}deg)`,
            maxHeight: '100%',
            maxWidth: rotationDeg % 180 === 0 ? '100%' : `${A4_RATIO * 100}%`,
            // blackOverlay is used to indicate negative states (deleted/read-only/...)
            '&:after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: theme =>
                alpha(theme.palette.common.black, blackOverlay),
            },
            boxShadow: `0px 0px 10px rgba(81, 81, 81, 0.04), inset 0px 0px 0px 1px #EEEEEE`,
            borderRadius: borderRadius('s'),
          }}
        >
          <Box
            component="img"
            src={data}
            alt={pageUrl}
            sx={{
              userSelect: 'none',
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: borderRadius.abs(shape.borderRadius, 's'),
            }}
          />
        </Box>
        {overlay}
      </Box>
      <Chip
        label={pageNumber}
        color={isCurrent ? 'info' : 'default'}
        sx={{ alignSelf: 'center' }}
        {...pageChipProps}
      />
    </Stack>
  );
};
